<template>
  <div class="energy-claim-wrapper">
    <!-- 领取条 -->
    <div class="energy-bar" v-if="availableNum > 0">
      <div class="energy-info">
        <span class="energy-num">
          🎁 {{ $t('Energy.Available') }}：65K x {{ availableNum }} {{ $t('Energy.Unit') }}
        </span>
        <span class="energy-desc">{{ $t('Energy.Description') }}</span>
      </div>
      <div class="energy-actions">
        <el-button
          class="energy-claim-btn"
          :disabled="availableNum === 0"
          @click="openDialog"
        >
          {{ availableNum === 0 ? $t('Energy.NoneAvailable') : $t('Energy.Claim') }}
        </el-button>
      </div>
    </div>

    <!-- 领取弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :close-on-click-modal="false"
      width="480px"
      @close="onDialogClose"
    >
      <div slot="title" class="energy-dialog-title">
        <span>{{ $t('Energy.DialogTitle') }}</span>
        <el-button
          class="energy-video-btn"
          @click="openVideoDialog"
        >
          {{ videoIntroText }}
        </el-button>
      </div>
      <!-- 钱包地址 -->
      <div class="form-label">{{ $t('Energy.WalletAddress') }}</div>
      <div class="addr-row">
        <CInput
          v-model="form.wallet_addons"
          :placeholder="$t('Energy.WalletAddress')"
          class="addr-input"
          @input="onManualInput"
        />
        <el-popover
          placement="bottom-end"
          width="380"
          trigger="click"
          v-model="popoverVisible"
        >
          <div class="wallet-dropdown">
            <div
              v-for="w in walletList"
              :key="w.wallet_id"
	              class="wallet-item"
	              @click="selectWallet(w)"
	            >
	              <div class="wallet-info">
	                <span class="wallet-addr">{{ w.wallet_addons }}</span>
	                <span v-if="w.tag" class="wallet-tag">{{ w.tag }}</span>
	              </div>
	              <button
	                class="wallet-delete-btn"
	                type="button"
	                :title="deleteWalletText"
	                @click.stop="confirmDeleteWallet(w)"
	              >
	                <i class="el-icon-delete"></i>
	              </button>
	            </div>
            <div v-if="walletList.length === 0" class="wallet-empty">
              No Data
            </div>
          </div>
          <el-button
            slot="reference"
            class="addr-select-btn"
          >
            {{ $t('Energy.SelectSavedAddress') }}
          </el-button>
        </el-popover>
      </div>

      <!-- 保存地址 -->
      <el-checkbox
        v-model="form.saveAddress"
        class="save-checkbox"
        @change="onSaveChange"
      >
        {{ $t('Energy.SaveAddress') }}
      </el-checkbox>

      <!-- 标签 -->
      <div v-if="form.saveAddress">
        <div class="form-label">{{ $t('Energy.AddressTag') }}</div>
        <CInput
          v-model="form.tag"
          :placeholder="$t('Energy.TagPlaceholder')"
        />
      </div>

      <!-- 提示区 -->
      <div class="tips-box">
        <div class="tips-title">{{ $t('Energy.CostComparison') }}</div>
        <table class="tips-table">
          <thead>
            <tr>
              <th></th>
              <th class="col-highlight">{{ $t('Energy.UsingEnergy') }}</th>
              <th>{{ $t('Energy.NotUsingEnergy') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ $t('Energy.Row1Label') }}</td>
              <td class="highlight">≈65K</td>
              <td>≈{{ $t('Energy.Row1Without') }}</td>
            </tr>
            <tr>
              <td>{{ $t('Energy.Row2Label') }}</td>
              <td class="highlight">≈131K</td>
              <td>≈{{ $t('Energy.Row2Without') }}</td>
            </tr>
          </tbody>
        </table>
        <ul class="tips-list">
          <li>{{ $t('Energy.Tip1') }}</li>
          <li>{{ $t('Energy.Tip2') }}</li>
          <li>{{ $t('Energy.Tip3') }}</li>
          <li>{{ $t('Energy.Tip4') }}</li>
          <li>{{ $t('Energy.ValidityHint') }}</li>
        </ul>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button class="cancel-btn" @click="dialogVisible = false">
          {{ $t('Energy.Cancel') }}
        </el-button>
        <el-button
          type="primary"
          :loading="sendLoading"
          :disabled="!canSend"
          @click="sendEnergy"
          class="confirm-btn"
        >
          {{ $t('Energy.Confirm') }}
        </el-button>
      </span>
    </el-dialog>

    <VideoPlayerDialog
      :title="videoDialogTitle"
      :visible.sync="videoDialogVisible"
      :src="currentVideoSrc"
    />
  </div>
</template>

<script>
import VideoPlayerDialog from '@/components/VideoPlayerDialog.vue'
import {
  getUserEnergy,
  getUserEnergyList,
  addUserEnergyWallet,
  sendUserEnergy,
  setUserEnergyWallet,
  deleteUserWalletAddressEnergy
} from '@/api/energyService'

const ENERGY_EN_VIDEO = 'https://files.fotoncard.com/video/energy-en.mp4'
const ENERGY_TW_VIDEO = 'https://files.fotoncard.com/video/energy-tw.mp4'

export default {
  name: 'EnergyClaim',

  components: {
    VideoPlayerDialog
  },

  props: {},

  data() {
    return {
      availableNum: 0,
      dialogVisible: false,
      videoDialogVisible: false,
      sendLoading: false,
      popoverVisible: false,
      walletList: [],
      form: {
        wallet_addons: '',
        saveAddress: false,
        tag: '',
        wallet_id: ''
      }
    }
  },

  computed: {
    canSend() {
      return this.form.wallet_addons.trim() !== '' && !this.sendLoading
    },

    isEnglish() {
      return this.$i18n && this.$i18n.locale === 'en'
    },

    currentVideoSrc() {
      return this.isEnglish ? ENERGY_EN_VIDEO : ENERGY_TW_VIDEO
    },

    deleteWalletText() {
      return this.isEnglish ? 'Delete' : '刪除'
    },

    deleteConfirmTitle() {
      return this.isEnglish ? 'Confirm Delete' : '確認刪除'
    },

    deleteConfirmMessage() {
      return this.isEnglish
        ? 'Are you sure you want to delete this wallet address?'
        : '確認刪除該錢包地址？'
    },

    deleteConfirmButtonText() {
      return this.isEnglish ? 'Confirm' : '確認'
    },

    deleteCancelButtonText() {
      return this.isEnglish ? 'Cancel' : '取消'
    },

    videoIntroText() {
      return this.isEnglish ? 'Tutorial' : '視頻介紹'
    },

    videoDialogTitle() {
      return this.isEnglish ? 'Tutorial' : '視頻介紹'
    }
  },

  methods: {
    fetchEnergy() {
      getUserEnergy().then((res) => {
        if (res.code === 200 && res.data) {
          this.availableNum = res.data.energy_count || 0
        }
      }).catch((err) => {
        // this.$message.error(err)
        console.log("getEnergy err", err)
      })
    },

    onManualInput() {
      // 一旦手動輸入，就代表不是選擇保存地址
      this.form.wallet_id = ''
    },

    fetchWalletList() {
      getUserEnergyList().then((res) => {
        if (res.code === 200 && res.data) {
          this.walletList = Array.isArray(res.data) ? res.data : []
        }
      }).catch(() => {
        this.walletList = []
      })
    },

    openDialog() {
      this.dialogVisible = true
      this.fetchWalletList()
    },

    openVideoDialog() {
      this.videoDialogVisible = true
    },

    onDialogClose() {
      this.resetForm()
    },

    resetForm() {
      this.form.wallet_addons = ''
      this.form.saveAddress = false
      this.form.tag = ''
      this.form.wallet_id = ''
    },

    selectWallet(wallet) {
      this.form.wallet_addons = wallet.wallet_addons || ''
      this.form.tag = wallet.tag || ''
      this.form.wallet_id = wallet.wallet_id || ''
      this.popoverVisible = false
    },

    confirmDeleteWallet(wallet) {
      if (!wallet || !wallet.wallet_id) return

      this.$confirm(this.deleteConfirmMessage, this.deleteConfirmTitle, {
        confirmButtonText: this.deleteConfirmButtonText,
        cancelButtonText: this.deleteCancelButtonText,
        type: 'warning',
        closeOnClickModal: false
      }).then(() => {
        this.deleteWallet(wallet)
      }).catch(() => {})
    },

    deleteWallet(wallet) {
      deleteUserWalletAddressEnergy({ wallet_id: wallet.wallet_id })
        .then((res) => {
          if (res.code === 200) {
            if (this.form.wallet_id === wallet.wallet_id) {
              // 刪除已選保存地址後，保留輸入框內容，但不能再帶已刪除的 wallet_id。
              this.form.wallet_id = ''
            }
            this.fetchWalletList()
          } else {
            this.$message.error(this.extractMsg(res))
          }
        })
        .catch((err) => {
          this.$message.error(err.message || 'Error')
        })
    },

    onSaveChange(checked) {
      if (!checked) {
        this.form.tag = ''
      }
    },

    // 發送能量
    sendEnergy() {
      const wallet_addons = this.form.wallet_addons.trim()

      if (!wallet_addons) {
        this.$message.error(this.$t('Energy.WalletAddress'))
        return
      }

      this.sendLoading = true
      sendUserEnergy({ wallet_id: this.form.wallet_id || '', wallet_addons })
        .then((res) => {
          if (res.code === 200) {
            this.$message.success(this.$t('Energy.Success'))
            if (this.form.saveAddress) {
              // const exists = this.walletList.some(w => w.wallet_addons === wallet_addons)
              // if (!exists) {
              //   addUserEnergyWallet({ tag: this.form.tag, wallet_addons }).catch(() => {})
              // }
              // 已保存地址修改标签
              if(this.form.wallet_id) {
                const currentWallet = this.walletList.find(w => w.wallet_id === this.form.wallet_id)

                if(currentWallet && currentWallet.tag !== this.form.tag) {
                  setUserEnergyWallet({wallet_id: this.form.wallet_id, tag: this.form.tag}).catch(() => {})
                }
              } else {
                // 新地址保存
                const exists = this.walletList.some(w => w.wallet_addons === wallet_addons)

                if(!exists) {
                  addUserEnergyWallet({ tag: this.form.tag, wallet_addons }).catch(() => {})
                }
              }

            }
            this.dialogVisible = false
            this.fetchEnergy()
          } else {
            this.$message.error(this.extractMsg(res))
          }
        })
        .catch((err) => {
          this.$message.error(err.message || 'Error')
        })
        .finally(() => {
          this.sendLoading = false
        })
    },

    extractMsg(res) {
      if (res && res.message) return res.message
      const lang = localStorage.getItem('admin_lang') === 'TC' ? 'tc' : 'en'
      if (res && res.data) {
        return res.data[lang] || res.data.en || ''
      }
      return ''
    }
  },

  mounted() {
    this.fetchEnergy()
  }
}
</script>

<style scoped>
/* ===== 领取条 ===== */
.energy-bar {
  background: #f0f9eb;
  border: 1px solid #e1f0d8;
  border-radius: 5px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.energy-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.energy-num {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.energy-desc {
  font-size: 12px;
  color: #909399;
}

.energy-dialog-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.energy-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.energy-video-btn {
  height: 28px;
  padding: 5px 12px !important;
  color: #00bea4 !important;
  border: none !important;
  background: transparent !important;
  border-radius: 4px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  box-shadow: none !important;
  white-space: nowrap;
}

.energy-video-btn:hover,
.energy-video-btn:focus {
  color: #00a892 !important;
  border: none !important;
  background: #f0fffc !important;
}

.energy-claim-btn {
  background: #00bea4 !important;
  color: #fff !important;
  border: none !important;
  padding: 8px 24px !important;
  border-radius: 4px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  white-space: nowrap;
  flex-shrink: 0;
}

.energy-claim-btn:hover {
  background: #00a892 !important;
}

.energy-claim-btn.is-disabled,
.energy-claim-btn.is-disabled:hover,
.energy-claim-btn.is-disabled:focus {
  background: #a0d5cd !important;
  color: #fff !important;
  cursor: not-allowed;
}

/* ===== 弹窗 ===== */
.form-label {
  font-size: 14px;
  color: #303133;
  margin-bottom: 8px;
}

.addr-row {
  display: flex;
  gap: 8px;
  /*align-items: center;
  margin-bottom: 16px;*/
}

.addr-input {
  flex: 1;
}

.addr-input >>> .form-group {
  margin-bottom: 0;
}

.addr-input >>> .form-control {
  height: 38px;
}

.addr-select-btn {
  white-space: nowrap;
  flex-shrink: 0;
  height: 38px;
  padding: 0 16px;
  line-height: 1;
}

.addr-select-btn:hover {
  color: #00bea4 !important;
  border-color: #00bea4 !important;
}

.save-checkbox {
  margin-bottom: 16px;
}

/* ===== 钱包下拉 ===== */
.wallet-dropdown {
  max-height: 200px;
  overflow-y: auto;
  margin: -12px;
}

.wallet-item {
  padding: 10px 14px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #f5f7fa;
}

.wallet-item:last-child {
  border-bottom: none;
}

.wallet-item:hover {
  background: #f5f7fa;
}

.wallet-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.wallet-addr {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #303133;
  word-break: break-all;
}

.wallet-tag {
  font-size: 11px;
  color: #00bea4;
  /* background: #e8f8f4; */
  /* padding: 1px 8px; */
  border-radius: 10px;
  flex-shrink: 0;
  /* margin-left: 12px; */
}

.wallet-delete-btn {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  outline: none;
  border-radius: 50%;
  background: transparent;
  color: #f56c6c;
  cursor: pointer;
  line-height: 24px;
  text-align: center;
}

.wallet-delete-btn:hover {
  background: #fef0f0;
  color: #f56c6c;
}

.wallet-empty {
  padding: 24px;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}

/* ===== 提示区 ===== */
.tips-box {
  background: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 6px;
  padding: 14px 16px;
  margin-top: 16px;
}

.tips-title {
  font-size: 13px;
  font-weight: 600;
  color: #e6a23c;
  margin-bottom: 12px;
}

.tips-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 12px;
}

.tips-table th {
  background: #fef0e6;
  padding: 8px 12px;
  text-align: center;
  font-weight: 600;
  color: #606266;
  border: 1px solid #f5dab1;
}

.tips-table th.col-highlight {
  color: #00bea4;
}

.tips-table td {
  padding: 8px 12px;
  text-align: center;
  border: 1px solid #f5dab1;
  color: #303133;
}

.tips-table td:first-child {
  text-align: left;
  font-weight: 500;
}

.tips-table td.highlight {
  color: #00bea4;
  font-weight: 600;
}

.tips-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tips-list li {
  font-size: 12px;
  color: #909399;
  line-height: 1.9;
  padding-left: 14px;
  position: relative;
}

.tips-list li::before {
  content: '•';
  position: absolute;
  left: 4px;
  color: #e6a23c;
}

/* ===== 底部按钮 ===== */
.confirm-btn {
  background: #00bea4 !important;
  border-color: #00bea4 !important;
}

.confirm-btn:hover,
.confirm-btn:focus {
  background: #00a892 !important;
  border-color: #00a892 !important;
}

.cancel-btn:hover {
  color: #00bea4 !important;
  border-color: #c0c4cc !important;
}

@media (max-width: 768px) {
  .energy-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .energy-actions {
    width: 100%;
  }

  .energy-claim-btn {
    flex: 1;
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

}
</style>
